import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLingui } from "@lingui/react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePickupContacts } from "@/hooks/useChildContactRelationships";
import {
  PICKUP_PERSON_MAX_LENGTH,
  PICKUP_REASON_MAX_LENGTH,
  PICKUP_SOURCE_AUTHORIZED,
  PICKUP_SOURCE_DOOR_MODE,
  PICKUP_SOURCE_NOT_ON_LIST,
  PICKUP_SOURCE_PARENT_NOTICE,
  type ActivityFormData,

  type ActivityFormInput,
} from "@/schemas/activityPayloadSchemas";
import type { ChildContactRelationshipWithDetailsResponse } from "@/types/childContactRelationship";

interface CheckOutPickupFieldsProps {
  form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData>;
  childId: string;
}

/**
 * CheckOutPickupFields — the collector record for a web-recorded check-out
 * (issue #993).
 *
 * A check-out is a handover, and the pickup record is the safeguarding artefact
 * the Kita relies on when a handover is disputed. The employee app has enforced
 * a recorded collector since #993; this is the same contract on the web surface,
 * so an activity recorded or corrected here cannot leave the record without one.
 *
 * Two paths, mutually exclusive and distinguishable in the stored payload:
 *
 * - **Path A** — pick from the child's authorised pickup list. Writes
 *   `pickup_source: "authorized_contact"` plus `pickup_contact_id`, and
 *   `pickup_person` from the chosen contact's name. The server re-resolves the
 *   id against the same authorised set and rewrites `pickup_person` from it, so
 *   a stale or invented id is a 400 rather than an unverifiable claim.
 * - **Path B** — someone not on the list. Writes `pickup_source: "not_on_list"`,
 *   a free-text collector name, and a mandatory explanation. This raises a
 *   pending `pickup_exception` notice for admins server-side.
 *
 * Two recorded paths are read-only here: a door-mode check-out (#2023) has no
 * collector, and a parent-notice check-out (#1876) names the collector the
 * family announced, which the server re-resolves from `pickup_notice_id`.
 *
 * The legacy `picked_up_by` field is deliberately not rendered: it stored a bare
 * contact id, was never validated, and is superseded by the pair above.
 */
export function CheckOutPickupFields({
  form,
  childId,
}: CheckOutPickupFieldsProps) {
  const { i18n } = useLingui();
  const { data: pickupContacts, isLoading } = usePickupContacts(childId);

  const pickupSource = form.watch("data.pickup_source") as string | undefined;
  const isNotOnList = pickupSource === PICKUP_SOURCE_NOT_ON_LIST;

  const contacts = useMemo<ChildContactRelationshipWithDetailsResponse[]>(
    () => pickupContacts ?? [],
    [pickupContacts],
  );

  const contactName = useCallback(
    (contact: ChildContactRelationshipWithDetailsResponse) =>
      `${contact.contactFirstname ?? ""} ${contact.contactLastname ?? ""}`.trim(),
    [],
  );

  /**
   * Switching paths clears the other path's fields, so the payload can never
   * carry a contradictory pair (the server rejects those outright).
   */
  const handlePathToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        form.setValue("data.pickup_source", PICKUP_SOURCE_NOT_ON_LIST);
        form.setValue("data.pickup_contact_id", undefined);
        form.setValue("data.pickup_person", undefined);
      } else {
        form.setValue("data.pickup_source", undefined);
        form.setValue("data.pickup_exception_reason", undefined);
        form.setValue("data.pickup_person", undefined);
      }
      void form.trigger("data");
    },
    [form],
  );

  const handleContactSelect = useCallback(
    (contactId: string) => {
      const contact = contacts.find((c) => c.contactId === contactId);
      form.setValue("data.pickup_source", PICKUP_SOURCE_AUTHORIZED);
      form.setValue("data.pickup_contact_id", contactId);
      // The name is what a later reader of the record sees; the server rewrites
      // it from the resolved contact anyway, so this is the optimistic value.
      form.setValue(
        "data.pickup_person",
        contact ? contactName(contact) : undefined,
      );
      form.setValue("data.pickup_exception_reason", undefined);
      void form.trigger("data");
    },
    [contacts, contactName, form],
  );

  const selectPlaceholder = isLoading
    ? i18n._({
        id: "activities.form.loadingContacts",
        message: "Loading contacts...",
      })
    : contacts.length === 0
      ? i18n._({
          id: "activities.form.noPickupContacts",
          message: "No authorised pickup contacts",
        })
      : i18n._({
          id: "activities.form.selectContact",
          message: "Select who picked up the child...",
        });

  // Door mode (#2023): the child checked itself out on the group tablet. There is no
  // collector to record or change, so the source is shown read-only.
  if (pickupSource === PICKUP_SOURCE_DOOR_MODE) {
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {i18n._({ id: "activities.form.pickedUpBy", message: "Picked Up By" })}
        </p>
        <p className="text-sm text-muted-foreground">
          {i18n._({
            id: "activities.pickupSource.doorMode",
            message: "Self-reported (door mode)",
          })}
        </p>
      </div>
    );
  }

  // Parent notice (#1876): the family announced the collector; the server resolves the
  // name from the notice, so an edit keeps the record as it is.
  if (pickupSource === PICKUP_SOURCE_PARENT_NOTICE) {
    const collector = form.watch("data.pickup_person") as string | undefined;
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {i18n._({ id: "activities.form.pickedUpBy", message: "Picked Up By" })}
        </p>
        {collector && <p className="text-sm">{collector}</p>}
        <p className="text-sm text-muted-foreground">
          {i18n._({
            id: "activities.pickupSource.parentNotice",
            message: "Announced by the family",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Path A — the authorised pickup list. Stays the first control. */}
      {!isNotOnList && (
        <FormField
          control={form.control}
          name="data.pickup_contact_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {i18n._({
                  id: "activities.form.pickupPerson.required",
                  message: "Picked up by (required)",
                })}
              </FormLabel>
              <Select
                onValueChange={handleContactSelect}
                value={(field.value as string) ?? ""}
                disabled={isLoading || contacts.length === 0}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={selectPlaceholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {contacts.map((contact) => (
                    <SelectItem
                      key={contact.contactId}
                      value={contact.contactId}
                    >
                      {contactName(contact)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contacts.length === 0 && !isLoading && (
                <FormDescription>
                  {i18n._({
                    id: "activities.form.noPickupContacts.help",
                    message:
                      "You can still record the check-out below by naming who collected the child and why.",
                  })}
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* The escape hatch. Never a dead end, even with zero authorised contacts. */}
      <FormField
        control={form.control}
        name="data.pickup_source"
        render={() => (
          <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={isNotOnList}
                onCheckedChange={(checked) =>
                  handlePathToggle(checked === true)
                }
                aria-label={i18n._({
                  id: "activities.form.pickupNotOnList",
                  message: "Collected by someone not on the authorised list",
                })}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-normal">
                {i18n._({
                  id: "activities.form.pickupNotOnList",
                  message: "Collected by someone not on the authorised list",
                })}
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      {/* Path B — the audited exception. */}
      {isNotOnList && (
        <div className="space-y-4 rounded-md border border-destructive/40 p-4">
          <Badge variant="destructive">
            {i18n._({
              id: "workflow.payload.notOnList",
              message: "Not on the authorised list",
            })}
          </Badge>

          <FormDescription>
            {i18n._({
              id: "activities.form.pickupException.intro",
              message:
                "Record who collected the child and why they were permitted to. A childcare admin will be notified.",
            })}
          </FormDescription>

          <FormField
            control={form.control}
            name="data.pickup_person"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {i18n._({
                    id: "activities.pickupException.name.label",
                    message: "Who collected the child?",
                  })}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={(field.value as string) ?? ""}
                    maxLength={PICKUP_PERSON_MAX_LENGTH}
                    placeholder={i18n._({
                      id: "activities.pickupException.name.placeholder",
                      message: "Full name",
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="data.pickup_exception_reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {i18n._({
                    id: "activities.pickupException.reason.label",
                    message: "Why were they permitted to?",
                  })}
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={(field.value as string) ?? ""}
                    maxLength={PICKUP_REASON_MAX_LENGTH}
                    rows={3}
                    placeholder={i18n._({
                      id: "activities.pickupException.reason.placeholder",
                      message:
                        "e.g. mother phoned at 16:40 and authorised the grandmother",
                    })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}

export default CheckOutPickupFields;
